import { TransactionCode } from '@treasury/domain/accounts';
import { CompanyAccount } from '@treasury/domain/company/company-account.entity';
import { ReportFilters } from '@treasury/domain/reports/report-filters.entity';
import { type SelectItemVm } from '@treasury/presentation/components/forms';
import { CurrencyRangeType } from '@treasury/presentation/components/tm-currency-range';
import { type DateRange, DateRangeType } from '@treasury/presentation/components/tm-date-range';

const defaultDateRange: DateRange = {
    rangeType: DateRangeType.Today,
};

export function mapAccountToVm(account: CompanyAccount): SelectItemVm<CompanyAccount> {
    if (!account.accountDisplayLabel) {
        throw new Error('Account display label is required');
    }

    return {
        label: account.accountDisplayLabel,
        value: account,
    };
}

export function mapPaymentTypeToVm(paymentType: TransactionCode): SelectItemVm<TransactionCode> {
    return {
        label: paymentType.name,
        value: paymentType,
    };
}

export class ReportFiltersVm extends ReportFilters {
    // Stored rather than leveraging entity getters to maintain references for tm-select components
    public readonly allAccountItems = this.accounts.map(mapAccountToVm);

    public readonly accountTypeItems = [
        {
            label: 'Checking',
            value: ['Checking'],
        },
        {
            label: 'Savings',
            value: ['Savings'],
        },
        {
            label: 'Checking and Savings',
            value: ['Checking', 'Savings'],
        },
        {
            label: 'Loan',
            value: ['Loan'],
        },
    ];

    private _selectedAccounts = [...this.filteredAccounts];

    // TODO: Remove this once TmSelect supports non-string values
    public get accountTypeItemsString() {
        return this.accountTypeItems.map(a => ({ label: a.label, value: a.value.join(',') }));
    }

    public get accountItems() {
        return this.accounts?.map(acc => ({
            label: acc.accountDisplayLabel,
            value: acc,
        }));
    }

    public get accountTypes() {
        return super.accountTypes;
    }

    public set accountTypes(value: string | string[]) {
        super.accountTypes = Array.isArray(value) ? value : [value];
        this.selectedAccounts = this.filteredAccounts;
    }

    public get filteredAccounts() {
        return this.accounts.filter(a => this.accountTypes.includes(a.type ?? ''));
    }

    public get selectedAccounts() {
        return this._selectedAccounts;
    }

    public set selectedAccounts(value) {
        this._selectedAccounts = value;
        this.accounts = [...value];
    }

    public get initialPaymentTypeItems() {
        return this.paymentTypes.map(pt => ({ label: pt.name, value: pt }));
    }

    public get initialAccountType() {
        return ['Checking', 'Savings'];
    }

    public get transactionSortByItems() {
        return [
            {
                label: 'Account Number',
                value: 'AccountNumber',
            },
            {
                label: 'Account Name',
                value: 'AccountName',
            },
            {
                label: 'Account Type',
                value: 'AccountType',
            },
            {
                label: 'Transaction Type',
                value: 'transactionCodeDescription',
            },
            {
                label: 'Debits',
                value: 'Debits',
            },
            {
                label: 'Credits',
                value: 'Credits',
            },
        ];
    }

    public get sortByItems() {
        return [
            {
                label: 'Account Number',
                value: 'AccountNumber',
            },
            {
                label: 'Account Name',
                value: 'AccountName',
            },
            {
                label: 'Account Type',
                value: 'AccountType',
            },
            {
                label: 'Current Balance',
                value: 'CurrentBalance',
            },
            {
                label: 'Available Balance',
                value: 'AvailableBalance',
            },
            {
                label: 'Collected Balance',
                value: 'CollectedBalance',
            },
            {
                label: 'Debits',
                value: 'Debits',
            },
            {
                label: 'Credits',
                value: 'Credits',
            },
        ];
    }

    public get sortByInitial() {
        return this.sortBy;
    }

    // TODO: Change the values to bool once TmRadioGroup supports non-string values
    public get isAscendingSortOptions() {
        return [
            { label: 'Ascending', value: 'true' },
            { label: 'Descending', value: 'false' },
        ];
    }

    // TODO: Remove this once TmRadioGroup supports non-string values
    public get displayIsAscendingSort(): string {
        return this.isAscendingSort.toString();
    }

    // TODO: Remove this once TmRadioGroup supports non-string values
    public set displayIsAscendingSort(value) {
        this.isAscendingSort = value === 'true' ? true : false;
    }

    public get initialDateRange() {
        if (this.fromDate && this.toDate) {
            return {
                rangeType: DateRangeType.DateRange,
                startDate: new Date(this.fromDate),
                endDate: new Date(this.toDate),
            };
        }
        if (this.specificDate) {
            return {
                rangeType: DateRangeType.SpecificDate,
                startDate: new Date(this.specificDate),
            };
        }
        return defaultDateRange;
    }

    public get checkNumberOperatorItems() {
        return [
            {
                label: 'Specific Check Number',
                value: 'Specific Check Number',
            },
            {
                label: 'Check Number Range',
                value: 'Check Number Range',
            },
        ];
    }

    public get initialCheckNumberOperator() {
        return 'Specific Check Number';
    }

    public get initialCheckNumber() {
        return '';
    }

    public get initialCurrencyRange() {
        return {
            rangeType: CurrencyRangeType.AllAmounts,
        };
    }

    public get transactionDirectionItems() {
        return [
            {
                label: 'Inbound',
                value: 'Inbound',
            },
            {
                label: 'Outbound',
                value: 'Outbound',
            },
            {
                label: 'Both',
                value: 'Both',
            },
        ];
    }

    public get selectedTransactionDirection() {
        return this.transactionDirection;
    }

    public set selectedTransactionDirection(value) {
        this.transactionDirection = value;
    }

    public get initialTransactionDirection() {
        return 'Both';
    }

    public get transactionTypeItems() {
        return [
            {
                label: 'Debit',
                value: 'Debit',
            },
            {
                label: 'Credit',
                value: 'Credit',
            },
            {
                label: 'Both',
                value: 'Both',
            },
        ];
    }

    public get selectedTransactionType() {
        return this.transactionType;
    }

    public set selectedTransactionType(value) {
        this.transactionType = value;
    }

    public get initialTransactionType() {
        return 'Both';
    }

    public get statusItems() {
        return [
            { label: 'Pending', value: 'false' },
            { label: 'Posted', value: 'true' },
        ];
    }

    public get selectedStatus() {
        return this.postedTransactions;
    }

    public set selectedStatus(value) {
        this.postedTransactions = value;
    }

    public get initialStatus() {
        return this.postedTransactions;
    }

    public get amountTypeItems() {
        return [
            {
                label: 'Debit',
                value: 'Debit',
            },
            {
                label: 'Credit',
                value: 'Credit',
            },
            {
                label: 'Both',
                value: 'Both',
            },
        ];
    }

    public reset() {
        return;
    }
}
